/**
 * WebSocket Provider
 *
 * Manages WebSocket connections and real-time data integration
 * Connects WebSocket events to Jotai atoms and TanStack Query cache
 */

import { createContext, useContext, useEffect, useRef, useState, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  currentFactoryIdAtom,
  webSocketConnectionAtom,
  setConnectionStatusAtom,
  setConnectedFactoryAtom,
  addEventToStreamAtom,
  addPositionBatchAtom,
  positionBufferActiveAtom,
  positionStatsAtom,
  clearPositionsAtom,
} from '@/stores/atoms';
import { webSocketManager } from '@/lib/websocket';
import type { DomainEvent, PositionEvent } from '@/lib/domain-events';
import { handleQueryError } from '@/lib/error-handling';
import { logger } from '@/lib/logger';
import { getSocketAuthToken } from '@/lib/socket-auth-token';
import { auth } from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

// WebSocket context interface
interface WebSocketContextValue {
  // Connection state
  isConnected: boolean;
  connectionHealth: { isHealthy: boolean; lastUpdate: Date | null };

  // Manual connection control
  connect: (factoryId: string) => Promise<void>;
  disconnect: () => void;

  // Subscription management
  subscribe: (type: 'POSITIONS' | 'ALERTS' | 'ASSETS' | 'ZONES') => void;
  unsubscribe: (type: 'POSITIONS' | 'ALERTS' | 'ASSETS' | 'ZONES') => void;

  // Event listeners
  addEventListener: (eventType: string, handler: (event: DomainEvent) => void) => () => void;
}

// Create context
const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// Custom hook to use WebSocket context
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

// WebSocket provider component
export function WebSocketProvider({ children }: PropsWithChildren): React.JSX.Element {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  // Track Firebase auth readiness so we can retry WebSocket connection
  const [authReady, setAuthReady] = useState(!!auth.currentUser);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setAuthReady(!!user);
    });
    return unsubscribe;
  }, []);

  // Atom values and setters
  const currentFactoryId = useAtomValue(currentFactoryIdAtom);
  // Simplified: user will be handled by AuthProvider later
  const connectionState = useAtomValue(webSocketConnectionAtom);
  const connectionHealth = {
    isHealthy: connectionState.status === 'connected',
    lastUpdate: connectionState.lastConnected,
  };

  const setConnectionStatus = useSetAtom(setConnectionStatusAtom);
  const setConnectedFactory = useSetAtom(setConnectedFactoryAtom);
  const clearPositions = useSetAtom(clearPositionsAtom);
  const addEventToStream = useSetAtom(addEventToStreamAtom);
  const addPositionBatch = useSetAtom(addPositionBatchAtom);
  const addPositionBatchRef = useRef(addPositionBatch);
  addPositionBatchRef.current = addPositionBatch;
  const bufferActive = useAtomValue(positionBufferActiveAtom);
  const bufferActiveRef = useRef(bufferActive);
  bufferActiveRef.current = bufferActive;
  const currentPositionStats = useAtomValue(positionStatsAtom);
  const setPositionStats = useSetAtom(positionStatsAtom);
  const positionStatsRef = useRef(currentPositionStats);
  positionStatsRef.current = currentPositionStats;

  // Refs for cleanup
  const eventHandlersRef = useRef<(() => void)[]>([]);

  // Auto-connect when factory changes or auth becomes ready
  useEffect(() => {
    if (currentFactoryId && authReady) {
      // Connect to factory
      const connectToFactory = async (): Promise<void> => {
        try {
          clearPositions();
          setConnectionStatus('connecting');
          setConnectedFactory(currentFactoryId);

          await webSocketManager.connect(currentFactoryId, getSocketAuthToken);

          // Subscribe to all relevant data streams
          webSocketManager.subscribe('POSITIONS');
          webSocketManager.subscribe('ALERTS');
          webSocketManager.subscribe('ASSETS');
          webSocketManager.subscribe('ZONES');

          setConnectionStatus('connected');
        } catch (error) {
          setConnectionStatus('error');
          const appError = handleQueryError(error, ['websocket', 'connect']);
          logger.logWebSocketError('WebSocket connection failed', appError);
        }
      };

      connectToFactory();
    } else if (!currentFactoryId) {
      // Disconnect when no factory is selected
      webSocketManager.disconnect();
      setConnectionStatus('disconnected');
    }
  }, [currentFactoryId, authReady, setConnectionStatus, setConnectedFactory]);

  // Set up event handlers for domain events
  useEffect(() => {
    // Clean up any previously registered handlers before re-registering
    eventHandlersRef.current.forEach(cleanup => cleanup());
    eventHandlersRef.current = [];

    // Position events are handled by usePositionBuffer (buffered at 30fps with coordinate transform)
    // Only log them to the event stream here
    const positionHandler = webSocketManager.addEventListener('POSITION_RECEIVED', event => {
      addEventToStream(event);
    });

    const batchHandler = webSocketManager.addEventListener('POSITIONS_BATCH_RECEIVED', event => {
      addEventToStream(event);

      const positionEvent = event as PositionEvent;
      const positions = positionEvent.positions ?? [];

      if (!bufferActiveRef.current && positions.length > 0) {
        // No usePositionBuffer mounted (e.g. Dashboard) — feed raw positions
        // directly so derived atoms (alerts, staleness) stay up to date.
        logger.warn(`[WSP] Feeding ${positions.length} positions to atoms (bufferActive=false)`);
        // addPositionBatch also stamps positionStatsAtom.lastUpdateTime as Date.now()
        // so the connection badge stays accurate on all pages.
        addPositionBatchRef.current(positions);
      } else if (bufferActiveRef.current) {
        // usePositionBuffer is active (Tracking page) — it handles coordinate
        // transforms and calls addPositionBatch itself. Only stamp lastUpdateTime
        // so connection status badges work everywhere.
        setPositionStats({ ...positionStatsRef.current, lastUpdateTime: new Date() });
      }
    });

    // Asset events
    const assetHandler = webSocketManager.addEventListener('ASSET_UPDATED', event => {
      addEventToStream(event);
      // Cache invalidation is handled in websocket.ts
    });

    // Alert events
    const alertHandler = webSocketManager.addEventListener('ALERT_TRIGGERED', event => {
      addEventToStream(event);
      // Show a toast for each real-time alert
      const alertData = (event as { alert?: { message?: string; severity?: string } }).alert;
      const message = alertData?.message ?? tRef.current('alerts.triggered_fallback');
      const severity = alertData?.severity ?? 'info';
      if (severity === 'error') {
        toast.error(message, { duration: 8000 });
      } else if (severity === 'warning') {
        toast.warning(message, { duration: 6000 });
      } else {
        toast.info(message, { duration: 5000 });
      }
    });

    const alertResolvedHandler = webSocketManager.addEventListener('ALERT_RESOLVED', event => {
      addEventToStream(event);
    });

    // Connection events
    const connectedHandler = webSocketManager.addEventListener('WEBSOCKET_CONNECTED', event => {
      setConnectionStatus('connected');
      addEventToStream(event);
    });

    const disconnectedHandler = webSocketManager.addEventListener(
      'WEBSOCKET_DISCONNECTED',
      event => {
        setConnectionStatus('disconnected');
        addEventToStream(event);
      }
    );

    const reconnectedHandler = webSocketManager.addEventListener('WEBSOCKET_RECONNECTED', event => {
      setConnectionStatus('connected');
      addEventToStream(event);
    });

    // Global error handler
    const errorHandler = webSocketManager.addEventListener('ERROR_OCCURRED', event => {
      setConnectionStatus('error');
      addEventToStream(event);

      const errorEvent = event as DomainEvent & { error: unknown };
      const error = handleQueryError(errorEvent.error, ['websocket', 'event']);
      logger.logWebSocketError('WebSocket event error', error);
    });

    // Store cleanup functions
    eventHandlersRef.current = [
      positionHandler,
      batchHandler,
      assetHandler,
      alertHandler,
      alertResolvedHandler,
      connectedHandler,
      disconnectedHandler,
      reconnectedHandler,
      errorHandler,
    ];

    return (): void => {
      eventHandlersRef.current.forEach(cleanup => cleanup());
      eventHandlersRef.current = [];
    };
  }, [addEventToStream, setConnectionStatus]);

  // Manual connection control
  const connect = async (factoryId: string): Promise<void> => {
    try {
      setConnectionStatus('connecting');
      setConnectedFactory(factoryId);

      await webSocketManager.connect(factoryId, getSocketAuthToken);

      // Subscribe to all relevant data streams
      webSocketManager.subscribe('POSITIONS');
      webSocketManager.subscribe('ALERTS');
      webSocketManager.subscribe('ASSETS');
      webSocketManager.subscribe('ZONES');

      setConnectionStatus('connected');
    } catch (error) {
      setConnectionStatus('error');
      const appError = handleQueryError(error, ['websocket', 'connect']);
      logger.logWebSocketError('WebSocket connection failed', appError);
    }
  };

  const disconnect = (): void => {
    webSocketManager.disconnect();
    setConnectionStatus('disconnected');
  };

  // Subscription management
  const subscribe = (type: 'POSITIONS' | 'ALERTS' | 'ASSETS' | 'ZONES'): void => {
    webSocketManager.subscribe(type);
  };

  const unsubscribe = (type: 'POSITIONS' | 'ALERTS' | 'ASSETS' | 'ZONES'): void => {
    webSocketManager.unsubscribe(type);
  };

  // Event listener registration
  // React Compiler handles reference stability automatically
  const addEventListener = (
    eventType: string,
    handler: (event: DomainEvent) => void
  ): (() => void) => {
    return webSocketManager.addEventListener(eventType, handler);
  };

  // Context value
  const contextValue: WebSocketContextValue = {
    // State
    isConnected: connectionState.status === 'connected',
    connectionHealth,

    // Actions
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    addEventListener,
  };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
}
